import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { auth, db } from '../../firebase/firebase';
import StatCard from '../shared/StatCard';
import WIcon from '../shared/WIcon';

const formatDate = (value) => {
  if (!value) return '—';
  if (typeof value === 'string') return new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  if (value.toDate) return value.toDate().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  return new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const VendorOverview = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [offers, setOffers] = useState([]);
  const [stats, setStats] = useState({ totalOffers: 0, activeOffers: 0, companies: 0 });

  const load = useCallback(async () => {
    try {
      const vendorId = auth.currentUser?.uid;
      if (!vendorId) return;

      const [offersSnap, companiesSnap] = await Promise.all([
        getDocs(query(collection(db, 'offers'), where('vendorId', '==', vendorId))),
        getDocs(collection(db, 'companies')),
      ]);

      const list = offersSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      const today = new Date();
      const active = list.filter((o) => {
        if (!o.endDate) return true;
        return new Date(o.endDate) >= today;
      }).length;

      setOffers(list);
      setStats({
        totalOffers: list.length,
        activeOffers: active,
        companies: companiesSnap.size,
      });
    } catch (err) {
      console.error('Error loading vendor overview:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return <div style={{ padding: 40, color: '#64748b', fontSize: 14 }}>Loading dashboard…</div>;
  }

  const recentOffers = [...offers]
    .sort((a, b) => {
      const da = a.createdAt?.toDate?.() || new Date(a.createdAt || 0);
      const dbb = b.createdAt?.toDate?.() || new Date(b.createdAt || 0);
      return dbb - da;
    })
    .slice(0, 5);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <div className="loysta-stat-grid">
        <StatCard
          icon="tag"
          label="Active Offers"
          value={stats.activeOffers}
          sub={stats.activeOffers === 1 ? 'Live right now' : `${stats.totalOffers} total`}
          variant="brand"
        />
        <StatCard
          icon="eye"
          label="Offer Views"
          value="—"
          sub="Reporting coming soon"
          variant="blue"
        />
        <StatCard
          icon="check"
          label="Redemptions"
          value="—"
          sub="Reporting coming soon"
          variant="green"
        />
        <StatCard
          icon="building"
          label="Reachable Companies"
          value={stats.companies}
          sub="Across the Loysta network"
          dark
        />
      </div>

      <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>
        <div className="loysta-card" style={{ flex: 1, minWidth: 0 }}>
          <div className="loysta-card-head">
            <span className="loysta-card-title">Your Offers</span>
            <span
              className="loysta-card-link"
              onClick={() => navigate('/vendor/dashboard/offers')}
              role="button"
            >View all</span>
          </div>
          {recentOffers.length === 0 ? (
            <div style={{ padding: 32, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
              You haven't created any offers yet.{' '}
              <span
                onClick={() => navigate('/vendor/dashboard/offers')}
                style={{ color: '#00bf63', fontWeight: 600, cursor: 'pointer' }}
              >Create your first one</span>
            </div>
          ) : (
            <table className="loysta-table">
              <thead>
                <tr>
                  <th>Offer</th>
                  <th>Discount</th>
                  <th>Min tier</th>
                  <th>Active until</th>
                </tr>
              </thead>
              <tbody>
                {recentOffers.map((o) => {
                  const tier = o.minTierLevel || '1';
                  return (
                    <tr key={o.id} style={{ cursor: 'pointer' }} onClick={() => navigate('/vendor/dashboard/offers')}>
                      <td>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>{o.title}</div>
                        {o.description && (
                          <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2, maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {o.description}
                          </div>
                        )}
                      </td>
                      <td>
                        <span style={{ background: '#fef3c7', color: '#92400e', padding: '3px 10px', borderRadius: 6, fontSize: 12, fontWeight: 700 }}>
                          {o.discountPercentage || 0}%
                        </span>
                      </td>
                      <td>
                        <span className={`loysta-badge tier-${tier}`}>
                          <span className="dot" />
                          T{tier}
                        </span>
                      </td>
                      <td style={{ color: '#94a3b8', fontSize: 12 }}>{formatDate(o.endDate)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <div style={{ width: 280, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{
            background: '#0f172a',
            borderRadius: 12,
            padding: 20,
            position: 'relative',
            overflow: 'hidden',
          }}>
            <div style={{
              position: 'absolute', top: -20, right: -20,
              width: 100, height: 100, borderRadius: '50%',
              background: 'rgba(193,255,114,0.08)', pointerEvents: 'none',
            }} />
            <div style={{ fontSize: 13, fontWeight: 700, color: 'white', marginBottom: 6 }}>
              Ready to redeem an offer?
            </div>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.55)', marginBottom: 14, lineHeight: 1.55 }}>
              Open the QR scanner to validate an employee's code at the counter.
            </div>
            <button
              type="button"
              onClick={() => navigate('/vendor/dashboard/scanner')}
              style={{
                width: '100%',
                padding: '9px',
                background: 'rgba(193,255,114,0.12)',
                border: '1px solid rgba(193,255,114,0.25)',
                borderRadius: 7,
                color: '#c1ff72',
                fontSize: 12,
                fontWeight: 700,
                fontFamily: 'inherit',
                cursor: 'pointer',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(193,255,114,0.20)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(193,255,114,0.12)'; }}
            >
              <WIcon name="qrCode" size={14} color="#c1ff72" />
              Open QR Scanner
            </button>
          </div>

          <div className="loysta-card" style={{ padding: 18 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', marginBottom: 14 }}>
              Quick links
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {[
                { icon: 'tag', label: 'Manage offers', to: '/vendor/dashboard/offers' },
                { icon: 'qrCode', label: 'QR Scanner', to: '/vendor/dashboard/scanner' },
                { icon: 'user', label: 'Business profile', to: '/vendor/dashboard/profile' },
              ].map((item) => (
                <button
                  key={item.to}
                  type="button"
                  onClick={() => navigate(item.to)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '10px 12px',
                    border: '1px solid #e2e8f0',
                    borderRadius: 8,
                    background: 'white',
                    color: '#334155',
                    fontSize: 13,
                    fontWeight: 600,
                    fontFamily: 'inherit',
                    cursor: 'pointer',
                    textAlign: 'left',
                    transition: 'all 0.15s',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#00bf63'; e.currentTarget.style.color = '#00bf63'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.color = '#334155'; }}
                >
                  <WIcon name={item.icon} size={15} />
                  {item.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VendorOverview;
